/* eslint-disable import/no-default-export */
import { normalizeBoard } from 'api/normalize';
// eslint-disable-next-line no-restricted-imports
import { Action, actionCreator } from 'util/actions';

// Action types
// eslint-disable-next-line no-restricted-imports
import {
  LOAD_BOARD_SUCCESS,
  LOAD_CARD_SUCCESS,
  LOAD_CLOSED_BOARDS_SUCCESS,
  LOAD_MEMBER_CARDS_SUCCESS,
  LOAD_MISSING_BOARDS_SUCCESS,
  LOAD_MY_BOARDS_SUCCESS,
  LoadBoardSuccessAction,
  LoadCardSuccessAction,
  LoadClosedBoardsSuccessAction,
  LoadMissingBoardsSuccessAction,
  LoadMyBoardsSuccessAction,
} from 'modules/loaders';

import {
  BOARD_SEARCH_SUCCESS,
  BoardSearchSuccessAction,
  GetSearchSuggestionsAction,
  PERFORM_SEARCH_SUCCESS,
  PerformSearchSuccessAction,
  SET_SEARCHED_SUGGESTIONS,
} from './search';

// eslint-disable-next-line no-restricted-imports
import { SOCKET_BOARD, SocketBoardAction } from 'modules/sockets';
// eslint-disable-next-line no-restricted-imports
import {
  CREATE_BOARD_SUBMIT_DONE,
  CreateBoardSubmitDoneAction,
} from 'modules/state/ui/create-menu';
// eslint-disable-next-line no-restricted-imports
import { BoardModel, BoardResponse } from 'types';
// eslint-disable-next-line no-restricted-imports
import { byId } from 'util/byId';
// eslint-disable-next-line no-restricted-imports
import { createReducer } from 'util/reducers';
import { createStateUpdater } from './util/create-state-updater';

export type BoardState = BoardModel[];
const SET_BOARD_NAME = Symbol('models/SET_BOARD_NAME');
export const CREATE_BOARD = Symbol('models/CREATE_BOARD');
const ADD_MEMBER_TO_BOARD = Symbol('models/ADD_MEMBER_TO_BOARD');
export const REMOVE_MEMBER_FROM_BOARD = Symbol(
  'models/REMOVE_MEMBER_FROM_BOARD',
);
export const SET_BOARD_CLOSED = Symbol('models/SET_BOARD_CLOSED');
export const DELETE_BOARD = Symbol('models/DELETE_BOARD');

type SetBoardNameAction = Action<typeof SET_BOARD_NAME, BoardResponse>;
type CreateBoardAction = Action<typeof CREATE_BOARD, BoardResponse>;
type AddMemberToBoardAction = Action<typeof ADD_MEMBER_TO_BOARD, BoardResponse>;
export type RemoveMemberFromBoardAction = Action<
  typeof REMOVE_MEMBER_FROM_BOARD,
  { idBoard: string; idMember: string }
>;
export type SetBoardClosedAction = Action<
  typeof SET_BOARD_CLOSED,
  BoardResponse
>;
export type DeleteBoardAction = Action<typeof DELETE_BOARD, string>;

export const setBoardNameAction = actionCreator<SetBoardNameAction>(
  SET_BOARD_NAME,
);
export const createBoardAction = actionCreator<CreateBoardAction>(CREATE_BOARD);
export const addMemberToBoardAction = actionCreator<AddMemberToBoardAction>(
  ADD_MEMBER_TO_BOARD,
);
export const removeMemberFromBoardAction = actionCreator<
  RemoveMemberFromBoardAction
>(REMOVE_MEMBER_FROM_BOARD);
export const setBoardClosedAction = actionCreator<SetBoardClosedAction>(
  SET_BOARD_CLOSED,
);
export const deleteBoardAction = actionCreator<DeleteBoardAction>(DELETE_BOARD);

// Reducer
const initialState: BoardState = [];
const updateBoardsState = createStateUpdater(normalizeBoard);

export default createReducer(initialState, {
  [CREATE_BOARD_SUBMIT_DONE](state, { payload }: CreateBoardSubmitDoneAction) {
    return updateBoardsState(state, payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [LOAD_MEMBER_CARDS_SUCCESS](state, { payload }: any) {
    return updateBoardsState(state, payload.boards);
  },
  [LOAD_MY_BOARDS_SUCCESS](state, { payload }: LoadMyBoardsSuccessAction) {
    return updateBoardsState(state, payload.boards);
  },
  [LOAD_MISSING_BOARDS_SUCCESS](
    state,
    { payload }: LoadMissingBoardsSuccessAction,
  ) {
    return updateBoardsState(state, payload);
  },
  [LOAD_BOARD_SUCCESS](state, { payload }: LoadBoardSuccessAction) {
    return updateBoardsState(state, payload);
  },
  [LOAD_CARD_SUCCESS](state, { payload }: LoadCardSuccessAction) {
    return updateBoardsState(state, payload);
  },
  [PERFORM_SEARCH_SUCCESS](state, { payload }: PerformSearchSuccessAction) {
    const { boards = [], cards = [] } = payload;

    return updateBoardsState(state, [
      ...boards,
      ...cards
        .map((card) => (card.board ? card.board : null))
        .filter((board): board is BoardResponse => board !== null),
    ]);
  },
  [BOARD_SEARCH_SUCCESS](state, { payload }: BoardSearchSuccessAction) {
    const existingById = byId(state);
    const newById = byId(payload.boards);

    return [
      ...state.map((board) => {
        const update = newById.get(board.id);

        return update ? normalizeBoard(update, board) : board;
      }),
      ...payload.boards
        .filter((response) => !existingById.has(response.id))
        .map((response) => normalizeBoard(response)),
    ];
  },
  [SET_SEARCHED_SUGGESTIONS](state, { payload }: GetSearchSuggestionsAction) {
    return updateBoardsState(state, payload.boards);
  },
  [SOCKET_BOARD](state, { payload }: SocketBoardAction) {
    return updateBoardsState(state, payload);
  },
  [SET_BOARD_NAME](state, { payload }: SetBoardNameAction) {
    return updateBoardsState(state, payload);
  },
  [CREATE_BOARD](state, { payload }: CreateBoardAction) {
    return updateBoardsState(state, payload);
  },
  [ADD_MEMBER_TO_BOARD](state, { payload }: AddMemberToBoardAction) {
    const { id, memberships } = normalizeBoard(payload);

    return state.map(
      (board) =>
        board.id !== id
          ? board
          : {
              ...board,
              memberships,
            },
    );
  },
  [REMOVE_MEMBER_FROM_BOARD](state, { payload }: RemoveMemberFromBoardAction) {
    const { idBoard, idMember } = payload;

    return state.map(
      (board) =>
        board.id !== idBoard
          ? board
          : {
              ...board,
              memberships: (board.memberships || []).filter(
                (membership) => membership.idMember !== idMember,
              ),
            },
    );
  },
  [SET_BOARD_CLOSED](state, { payload }: SetBoardClosedAction) {
    return updateBoardsState(state, payload);
  },
  [DELETE_BOARD](state, { payload }: DeleteBoardAction) {
    return updateBoardsState(state, { id: payload, deleted: true });
  },
  [LOAD_CLOSED_BOARDS_SUCCESS](
    state,
    { payload = [] }: LoadClosedBoardsSuccessAction,
  ) {
    return updateBoardsState(state, payload);
  },
});



// WEBPACK FOOTER //
// ./app/gamma/src/modules/state/models/boards.ts